/* eslint-disable complexity */
import { isNumber } from "lodash";
import { DatapointPerformancePeriodEnum } from "api/ux/src";
import { dateFromNullable, getDateQuarter, getDateString, isEndOfQuarter } from "common/date-utils";
import { esgScores } from "common/esg-helpers";
import { numberFormatters } from "common/number-formatters";
import { NumberFormatKind } from "common/types";
import { useCurrentDashboardEntityId, useGetEntityLatestDataDate, useGetSelectedReferenceDate } from "components/workspaces/hooks/use-current-dashboard";
import { language } from "language/default";
import { ChartSelectValueFormatKind, TenorOptions } from "store/current-dashboard/types";
import { useFundSeriesByIdSelector } from "components/pages/fund-series/api";
import { CommmonFormatEnum } from "datapoints";
export const getChartSelectValueProps = (selectedValues, widgetProps) => Object.values(selectedValues).map((value, index) => {
    const dataSelectProps = widgetProps.dataSelectProps[index].selectValues[value];
    return {
        ...dataSelectProps,
        valueFormat: dataSelectProps?.valueFormat || ChartSelectValueFormatKind.value
    };
});
const formatNumberValueAccordingToValueFormat = (valueFormat, value, options, formatUnit, previousValue) => {
    switch (valueFormat) {
        case ChartSelectValueFormatKind.multiple:
            return `${numberFormatters(value, NumberFormatKind.compact, {
                ...options,
                minimumFractionDigits: 2
            })}x`;
        case ChartSelectValueFormatKind.percent:
            return numberFormatters(value, NumberFormatKind.percent, options);
        case ChartSelectValueFormatKind.amount:
            return numberFormatters(value, NumberFormatKind.currency, options, formatUnit);
        case ChartSelectValueFormatKind.compact:
            return numberFormatters(value, NumberFormatKind.compact, options);
        case ChartSelectValueFormatKind.compactAmount:
            return `${numberFormatters(value, NumberFormatKind.compact, options, formatUnit)} ${options?.currency}`;
        case ChartSelectValueFormatKind.esg:
            return esgScores[esgScores.length - value - 1];
        case ChartSelectValueFormatKind.area:
            return numberFormatters(value, NumberFormatKind.area, options);
        case ChartSelectValueFormatKind.unit:
            return numberFormatters(value, NumberFormatKind.unit, options);
        case ChartSelectValueFormatKind.value:
            return numberFormatters(value, NumberFormatKind.compact);
        default:
            return previousValue;
    }
};
export const formatPointLabel = (value, axeFormat, valueUnit, options, formatUnit) => {
    let formattedValue = value?.toString();
    const valueFormat = isNaN(axeFormat) ? ChartSelectValueFormatKind[axeFormat] : axeFormat;
    switch (valueFormat) {
        case ChartSelectValueFormatKind.string:
        case ChartSelectValueFormatKind.entity:
            formattedValue = value;
            break;
        case ChartSelectValueFormatKind.date:
            formattedValue = getDateString(value?.year && value?.month && value?.day ? new Date(value.year, value.month - 1, value.day) : dateFromNullable(value));
            break;
        case ChartSelectValueFormatKind.boolean:
            if (typeof value === "boolean") {
                formattedValue = value ? "Yes" : "No";
            }
            else {
                formattedValue = "-";
            }
            break;
        default:
            break;
    }
    if (isNumber(value)) {
        formattedValue = formatNumberValueAccordingToValueFormat(valueFormat, value, options, formatUnit, formattedValue);
        if (formattedValue && formattedValue !== "" && valueUnit && valueUnit !== "") {
            return `${formattedValue} ${valueUnit}${value === 1 ? "" : "s"}`;
        }
    }
    return formattedValue;
};
export const formatPointLabelForAxis = (value, axeFormat, valueUnit, options) => {
    const keptOption = { currency: options?.currency, signDisplay: options?.signDisplay };
    if (axeFormat === ChartSelectValueFormatKind.amount || axeFormat === ChartSelectValueFormatKind.value) {
        return formatPointLabel(value, ChartSelectValueFormatKind.compact, valueUnit, keptOption);
    }
    if (axeFormat === ChartSelectValueFormatKind.multiple) {
        return formatPointLabel(value, ChartSelectValueFormatKind.multiple, valueUnit, keptOption);
    }
    return formatPointLabel(value, axeFormat, valueUnit, options);
};
export const getActualTooltipKeys = (tooltipKeys, dataSelectProps, selectedValues) => {
    const separator = "||";
    let actualTooltipKeys = tooltipKeys;
    if (selectedValues && tooltipKeys && dataSelectProps) {
        let tooltipKeysStr = tooltipKeys?.join(separator);
        Object.entries(selectedValues).forEach(([num, value]) => {
            tooltipKeysStr = tooltipKeysStr?.replace(`$${num}`, dataSelectProps[Number(num)].selectValues[value].displayName);
        });
        actualTooltipKeys = tooltipKeysStr.split(separator);
    }
    return actualTooltipKeys;
};
const getQuarterText = (date) => {
    if (!date) {
        return language.en.dashboard.noDateSet;
    }
    const quarter = date ? getDateQuarter(date) : 0;
    if (isEndOfQuarter(date)) {
        return `Q${quarter} ${date?.getFullYear()}`;
    }
    return getDateString(date);
};
export const getLabelForTenor = (tenor) => {
    switch (tenor) {
        case TenorOptions.customDateRange: return language.en.skeleton.settingsMenu.tenorOptions.customDateRange;
        case TenorOptions.fiveYears: return language.en.skeleton.settingsMenu.tenorOptions.fiveYears;
        case TenorOptions.Itd: return language.en.skeleton.settingsMenu.tenorOptions.Itd;
        case TenorOptions.oneMonth: return language.en.skeleton.settingsMenu.tenorOptions.oneMonth;
        case TenorOptions.oneQuarter: return language.en.skeleton.settingsMenu.tenorOptions.oneQuarter;
        case TenorOptions.oneYear: return language.en.skeleton.settingsMenu.tenorOptions.oneYear;
        case TenorOptions.sixMonths: return language.en.skeleton.settingsMenu.tenorOptions.sixMonths;
        case TenorOptions.tenYears: return language.en.skeleton.settingsMenu.tenorOptions.tenYears;
        case TenorOptions.threeYears: return language.en.skeleton.settingsMenu.tenorOptions.threeYears;
        case TenorOptions.twoYears: return language.en.skeleton.settingsMenu.tenorOptions.twoYears;
        case TenorOptions.Ytd: return language.en.skeleton.settingsMenu.tenorOptions.Ytd;
        default: return "";
    }
};
export const getShortLabelForTenor = (tenor) => {
    switch (tenor) {
        case TenorOptions.Itd: return language.en.skeleton.settingsMenu.tenorOptions.ItdShort;
        case TenorOptions.Ytd: return language.en.skeleton.settingsMenu.tenorOptions.YtdShort;
        default: return getLabelForTenor(tenor);
    }
};
export const getTenorOptionFromLabel = (label) => {
    switch (label) {
        case language.en.skeleton.settingsMenu.tenorOptions.fiveYears: return TenorOptions.fiveYears;
        case language.en.skeleton.settingsMenu.tenorOptions.oneQuarter: return TenorOptions.oneQuarter;
        case language.en.skeleton.settingsMenu.tenorOptions.oneYear: return TenorOptions.oneYear;
        case language.en.skeleton.settingsMenu.tenorOptions.tenYears: return TenorOptions.tenYears;
        case language.en.skeleton.settingsMenu.tenorOptions.threeYears: return TenorOptions.threeYears;
        case language.en.skeleton.settingsMenu.tenorOptions.twoYears: return TenorOptions.twoYears;
        case language.en.skeleton.settingsMenu.tenorOptions.Itd: return TenorOptions.Itd;
        default: return undefined;
    }
};
export const getLabelForPerformancePeriod = (performancePeriod) => {
    switch (performancePeriod) {
        case DatapointPerformancePeriodEnum.OneQuarter: return language.en.skeleton.settingsMenu.periodOfAnalysis.lastThreeMonths;
        case DatapointPerformancePeriodEnum.SixMonths: return language.en.skeleton.settingsMenu.periodOfAnalysis.lastSixMonths;
        case DatapointPerformancePeriodEnum.OneYear: return language.en.skeleton.settingsMenu.periodOfAnalysis.lastTwelveMonths;
        case DatapointPerformancePeriodEnum.Ytd: return language.en.skeleton.settingsMenu.periodOfAnalysis.ytd;
        case DatapointPerformancePeriodEnum.ThreeYears: return language.en.skeleton.settingsMenu.periodOfAnalysis.lastThreeYears;
        case DatapointPerformancePeriodEnum.FiveYears: return language.en.skeleton.settingsMenu.periodOfAnalysis.lastFiveYears;
        case DatapointPerformancePeriodEnum.TenYears: return language.en.skeleton.settingsMenu.periodOfAnalysis.lastTenYears;
        case DatapointPerformancePeriodEnum.Itd: return language.en.skeleton.settingsMenu.periodOfAnalysis.itd;
        default: return "";
    }
};
export const useGetDateText = (text, additionalData) => {
    const entityId = useCurrentDashboardEntityId();
    const fundSeries = useFundSeriesByIdSelector(entityId);
    const asOfDate = useGetSelectedReferenceDate();
    const entityLatestDataDate = useGetEntityLatestDataDate();
    // For FundSeries, use dateUploaded or fallback to referenceDate
    let dateToTake = fundSeries?.dateUploaded ?? fundSeries?.referenceDate;
    // Fallback to reference date if dateUploaded is not available
    if (!dateToTake) {
        dateToTake = asOfDate;
        if (additionalData?.shouldTakeLatestDataDate) {
            // Only take the latest data date if it is not after the asOfDate
            if (entityLatestDataDate && !(asOfDate && new Date(entityLatestDataDate) > asOfDate)) {
                dateToTake = new Date(entityLatestDataDate);
            }
        }
    }
    const quarterText = getQuarterText(dateToTake);
    return text?.replace("[date]", quarterText).replace("[dateValue]", quarterText);
};
export const useGetBenchmarkFooter = (text, additionalData) => {
    const asOfDate = useGetSelectedReferenceDate();
    let dateToTake = asOfDate;
    const entityLatestDataDate = useGetEntityLatestDataDate();
    if (additionalData?.shouldTakeLatestDataDate) {
        // Only take the latest data date if it is not after the asOfDate
        if (entityLatestDataDate && !(asOfDate && new Date(entityLatestDataDate) > asOfDate)) {
            dateToTake = new Date(entityLatestDataDate);
        }
    }
    const quarterText = getQuarterText(dateToTake);
    if (!additionalData?.benchmark && text && text?.indexOf("[benchmark]") >= 0) {
        return language.en.modal.privateBenchmarkSelection.noSelectedText;
    }
    const benchmarkText = additionalData?.benchmark ?? "";
    const benchmarkType = additionalData?.hasCustomBenchmark ? "" : language.en.widget.defaultBenchmarkLabel;
    return text?.replace("[quarter]", quarterText)?.replace("[benchmark]", benchmarkText)
        ?.replace("[benchmarkType]", benchmarkType);
};
export const mapValueFormatToCommonFormat = (format) => {
    switch (format) {
        case ChartSelectValueFormatKind.multiple:
            return CommmonFormatEnum.multiple;
        case ChartSelectValueFormatKind.percent:
            return CommmonFormatEnum.percent;
        case ChartSelectValueFormatKind.amount:
            return CommmonFormatEnum.currency;
        case undefined:
            return undefined;
        default:
            return CommmonFormatEnum.value;
    }
};
